import { SurveySupplementaryData } from "@common/ADAPT.Common.Model/organisation/survey-supplementary-data";
import { Validator } from "breeze-client";
import { BaseEntity } from "../base-entity";
import { BreezeModelBuilder } from "../breeze-model-builder";
import { Person } from "../person/person";
import { Organisation } from "./organisation";
import { OutstandingSurveyResponse } from "./outstanding-survey-response";
import {
    ResilientBusinessSurveyConfiguration,
    ResilientBusinessSurveyConfigurationBreezeModel,
} from "./resilient-business-survey-configuration";
import { SurveyResponse } from "./survey-response";
import { SurveyResult } from "./survey-result";
import { Team } from "./team";

export enum SurveyStatus {
    NotStarted = "NotStarted",
    Started = "Started",
    Ended = "Ended",
}

export enum SurveyEmailStatus {
    NotSent = "NotSent",
    InvitationProcessed = "InvitationProcessed",
    ReminderCompleted = "ReminderCompleted",
    InvitationCompleted = "InvitationCompleted",
    CleanupCompleted = "CleanupCompleted",
}

export enum SurveyType {
    EmployeeEngagement = "EmployeeEngagement",
    TeamAssessment = "TeamAssessment",
    ResilientBusinessAssessment = "ResilientBusinessAssessment",
    OrganisationDiagnostic = "OrganisationDiagnostic",
}

export const SurveyTypeLabel: { [type in SurveyType]: string } = {
    EmployeeEngagement: "Employee engagement",
    TeamAssessment: "Team assessment",
    ResilientBusinessAssessment: "Resilient business assessment",
    OrganisationDiagnostic: "Health check",
};

export const SurveyTypeManageLabel = "Manage Surveys";

export class Survey extends BaseEntity<Survey> {
    public surveyId!: number;
    public organisationId!: number;
    public surveyType!: SurveyType;
    public teamId?: number;
    public name!: string;
    public startTime!: Date;
    public endTime!: Date;
    public status!: SurveyStatus;
    public createdById!: number;
    public surveyorId!: number;
    public emailStatus!: SurveyEmailStatus;
    public message?: string;

    // nav properties
    public organisation?: Organisation;
    public team?: Team;
    public createdBy?: Person;
    public surveyor?: Person;
    public surveyResponses!: SurveyResponse[];
    public surveyResults!: SurveyResult[];
    public outstandingSurveyResponses!: OutstandingSurveyResponse[];
    public supplementaryData!: SurveySupplementaryData;
}

export const SurveyBreezeModel = new BreezeModelBuilder("Survey", Survey)
    .hasSource()
    .withIdField()
    .isOrganisationEntity()
    .alwaysFetchingNavigationProperty("surveyResults")
    .withEntityValidator(new Validator(
        "resilientBusinessAssessmentConfigurationValidator",
        (entity: Survey) => {
            if (entity.surveyType !== SurveyType.ResilientBusinessAssessment) {
                return true;
            }

            const configurations = entity.entityAspect.entityManager?.getEntities(ResilientBusinessSurveyConfigurationBreezeModel.toType) as ResilientBusinessSurveyConfiguration[] | undefined;
            const configuration = configurations?.find((item) => item.organisationId === entity.organisationId);

            return !!configuration?.leaderTeamId;
        },
        {
            messageTemplate: "A leader team must be configured for a Resilient Business Assessment survey.",
        }))
    .build();
